/**
 * Light or dark, and how the choice is remembered.
 *
 * Three states, not two: `system` follows `prefers-color-scheme` and is the
 * default, and the other two are a person overriding it. Collapsing `system`
 * into whichever value it currently resolves to loses the difference between
 * "they want dark" and "their machine is dark this evening".
 *
 * The attribute goes on `<html>` because the design system's token files
 * define both sets under `html[data-theme='dark']`. It is applied before React
 * mounts (see index.html) so there is no light flash on a dark machine.
 */

export type ThemeChoice = 'light' | 'dark' | 'system';

const KEY = 'mauvely.theme';

const media = () => window.matchMedia('(prefers-color-scheme: dark)');

export function savedTheme(): ThemeChoice {
  const raw = localStorage.getItem(KEY);
  return raw === 'light' || raw === 'dark' ? raw : 'system';
}

export function resolveTheme(choice: ThemeChoice): 'light' | 'dark' {
  return choice === 'system' ? (media().matches ? 'dark' : 'light') : choice;
}

export function applyTheme(choice: ThemeChoice): void {
  document.documentElement.setAttribute('data-theme', resolveTheme(choice));
}

export function setTheme(choice: ThemeChoice): void {
  if (choice === 'system') localStorage.removeItem(KEY);
  else localStorage.setItem(KEY, choice);
  applyTheme(choice);
}

/** Follow the system while the choice is `system`. Returns the unsubscribe. */
export function watchSystemTheme(): () => void {
  const mq = media();
  const onChange = () => {
    if (savedTheme() === 'system') applyTheme('system');
  };
  mq.addEventListener('change', onChange);
  return () => mq.removeEventListener('change', onChange);
}
